import React, { useEffect, useMemo, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import XGSIcon from 'ui-components/icon/xgsIcon';
import XGSIcons from 'ui-components/icon/xgsIcons';
import Loading from 'ui-components/loading/loading';
import XGSDateRangePickerWithPresets from 'ui-components/xgs-date/xgs-date-range-picker/xgsDateRangePickerWithPresets';
import { CloneManifestChangeLogProjection } from 'app/data/static-load/model';
import { useActivityLog } from '../hooks/useActivitylog';
import '../cloneWeek.scss';

type ActivityActionType = 'created' | 'updated' | 'deleted';

interface ActivityLogDetailRow {
    label: string;
    value: string;
}

interface ActivityLogEntry {
    id: string;
    userName: string;
    action: string;
    actionType: ActivityActionType;
    summary: string;
    subtitle?: string;
    changeDetail?: { label: string; oldValue: string; newValue: string };
    time: string;
    weekLabel: string;
    details: ActivityLogDetailRow[];
}

interface ActivityLogGroup {
    dateLabel: string;
    entries: ActivityLogEntry[];
}

interface ActivityLogSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const ACTIVITY_ACTION_OPTIONS = [
    { label: 'Create', value: 'CREATE' },
    { label: 'Update', value: 'UPDATE' },
    { label: 'Delete', value: 'DELETE' },
];

const parseChangeDetail = (description?: string) => {
    if (!description) return undefined;
    const match = description.match(/Changed\s+(.+?)\s*:\s*(.+?)\s*-->\s*(.+?)\.?$/);
    if (!match) return undefined;
    return { label: match[1].trim(), oldValue: match[2].trim(), newValue: match[3].trim() };
};

const getActionType = (userAction?: string): ActivityActionType => {
    switch (userAction) {
        case 'CREATE': return 'created';
        case 'DELETE': return 'deleted';
        default: return 'updated';
    }
};

const getActionLabel = (userAction?: string): string => {
    switch (userAction) {
        case 'CREATE': return 'Created';
        case 'UPDATE': return 'Updated';
        case 'DELETE': return 'Deleted';
        default: return userAction || 'Updated';
    }
};

const getSummary = (item: CloneManifestChangeLogProjection): string => {
    if (item.changeDescription) {
        const changeDetail = parseChangeDetail(item.changeDescription);
        if (changeDetail) return `Changed ${changeDetail.label}`;
        return item.changeDescription;
    }
    if (item.userAction === 'CREATE' && item.laneId) return `Created lane ${item.laneId}`;
    return item.userAction || 'Activity';
};

const formatWeekLabel = (weekStartDate?: string): string => {
    if (!weekStartDate) return '';
    const start = moment(weekStartDate);
    const end = moment(weekStartDate).add(6, 'days');
    if (start.month() === end.month()) {
        return `${start.format('MMM D')}-${end.format('D, YYYY')}`;
    }
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
};

const formatDateGroupLabel = (changedAt?: string): string => {
    if (!changedAt) return 'Unknown date';
    const date = moment(changedAt);
    const dateStr = date.format('MMMM D, YYYY');
    if (date.isSame(moment(), 'day')) return `Today — ${dateStr}`;
    if (date.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday — ${dateStr}`;
    return dateStr;
};

const buildSubtitle = (item: CloneManifestChangeLogProjection): string | undefined => {
    const parts: string[] = [];
    if (item.laneDetails) parts.push(item.laneDetails);
    if (item.carrier) parts.push(item.carrier);
    if (item.departure) parts.push(`Dep ${item.departure}`);
    return parts.length ? parts.join(' · ') : undefined;
};

const getUserDisplayName = (item: CloneManifestChangeLogProjection): string =>
    item.modifiedByName
    || item.updatedByName
    || item.createdByName
    || item.modifiedBy
    || item.updatedBy
    || item.createdBy
    || '—';

const buildDetails = (item: CloneManifestChangeLogProjection): ActivityLogDetailRow[] => {
    const changedAtFormatted = item.changedAt ? moment(item.changedAt).format('hh:mm A') : '—';
    const user = item.userAction === 'CREATE'
        ? (item.createdByName || item.modifiedByName || item.createdBy || item.modifiedBy || '—')
        : (item.updatedByName || item.modifiedByName || item.updatedBy || item.modifiedBy || '—');

    const rows: ActivityLogDetailRow[] = [
        { label: 'Lane ID', value: item.laneId || '—' },
        { label: 'Carrier', value: item.carrier || '—' },
    ];

    if (item.departure) {
        rows.push({ label: 'Departure', value: item.departure });
    }

    if (item.userAction === 'CREATE') {
        rows.push({ label: 'Created By', value: user });
        rows.push({ label: 'Created At', value: changedAtFormatted });
    } else {
        rows.push({ label: 'Changed By', value: user });
        rows.push({ label: 'Changed At', value: changedAtFormatted });
    }

    return rows;
};

const mapToEntry = (item: CloneManifestChangeLogProjection, index: number): ActivityLogEntry => ({
    id: `${item.planId ?? 'log'}-${item.changedAt ?? index}`,
    userName: getUserDisplayName(item),
    action: getActionLabel(item.userAction),
    actionType: getActionType(item.userAction),
    summary: getSummary(item),
    subtitle: buildSubtitle(item),
    changeDetail: parseChangeDetail(item.changeDescription),
    time: item.changedAt ? moment(item.changedAt).format('hh:mm A') : '—',
    weekLabel: formatWeekLabel(item.weekStartDate),
    details: buildDetails(item),
});

const groupActivityLogs = (items: CloneManifestChangeLogProjection[]): ActivityLogGroup[] => {
    const groups = new Map<string, ActivityLogEntry[]>();

    items.forEach((item, index) => {
        const dateLabel = formatDateGroupLabel(item.changedAt);
        const entry = mapToEntry(item, index);
        if (!groups.has(dateLabel)) groups.set(dateLabel, []);
        groups.get(dateLabel)!.push(entry);
    });

    return Array.from(groups.entries()).map(([dateLabel, entries]) => ({ dateLabel, entries }));
};

const ActivityLogSidebar: React.FC<ActivityLogSidebarProps> = ({ isOpen, onClose }) => {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('');
    const [dateFrom, setDateFrom] = useState<string | undefined>();
    const [dateTo, setDateTo] = useState<string | undefined>();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage, isError } = useActivityLog(
        {
            search: debouncedSearch || undefined,
            action: actionFilter || undefined,
            dateFrom: dateFrom ? dateFrom.toApiDateFormat() : undefined,
            dateTo: dateTo ? dateTo.toApiDateFormat() : undefined,
        },
        isOpen
    );

    const activityLogGroups = useMemo(() => {
        const items = data?.pages.flatMap((page) => page?.content ?? []) ?? [];
        return groupActivityLogs(items);
    }, [data]);

    const toggleExpanded = (id: string) => {
        setExpandedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 100 && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    return <SlideOutSidebar
        show={isOpen}
        onClose={onClose}
        header="Activity Log"
        size="small"
        className="filter-sidebar"
    >
        <Formik
            initialValues={{ search: '', action: '' }}
            onSubmit={() => {}}
        >
            {(formikProps: FormikProps<{ search: string; action: string }>) => (
                <Form>
                    <div className='activity-log-sidebar-form-container'>
                        <div className="activity-log-sidebar-form-filters-row">
                            <XGSFormInput
                                type="text"
                                name="search"
                                placeholder='Search by user, lane, action...'
                                labelMode={LabelModes.column}
                                className="activity-log-sidebar-form-input-search"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    formikProps.handleChange(e);
                                    setSearchInput(e.target.value);
                                }}
                            />
                            <XGSFormSelect
                                name="action"
                                labelMode={LabelModes.column}
                                controlled
                                options={ACTIVITY_ACTION_OPTIONS}
                                placeholder="All activities"
                                className="add-dispatch-sidebar-form-item__dropdown"
                                onValueChange={(option) => setActionFilter(String(option?.value ?? ''))}
                            />
                        </div>
                        <XGSDateRangePickerWithPresets
                            start={dateFrom}
                            end={dateTo}
                            onApply={(start, end) => {
                                setDateFrom(start);
                                setDateTo(end);
                            }}
                            maxDate={new Date()}
                            placeholder="Filter by date range"
                        />
                    </div>
                    <div className="activity-log-sidebar-list" onScroll={handleScroll}>
                        {isLoading ? (
                            <Loading isLoading={true} />
                        ) : isError ? (
                            <div className="activity-log-sidebar-list__empty">Unable to load activity log</div>
                        ) : activityLogGroups.length === 0 ? (
                            <div className="activity-log-sidebar-list__empty">No activity found</div>
                        ) : (
                            activityLogGroups.map((group) => (
                                <div key={group.dateLabel} className="activity-log-sidebar-list__group">
                                    <div className="activity-log-sidebar-list__date">{group.dateLabel}</div>

                                    {group.entries.map((entry) => {
                                        const isExpanded = expandedIds.has(entry.id);

                                        return (
                                            <div
                                                key={entry.id}
                                                className={`activity-log-sidebar-accordion${isExpanded ? ' activity-log-sidebar-accordion--expanded' : ''}`}
                                            >
                                                <button
                                                    type="button"
                                                    className="activity-log-sidebar-accordion__header"
                                                    onClick={() => toggleExpanded(entry.id)}
                                                    aria-expanded={isExpanded}
                                                >
                                                    <div className="activity-log-sidebar-accordion__header-main">
                                                        <span className="activity-log-sidebar-accordion__user">{entry.userName}</span>
                                                        <span className={`activity-log-sidebar-accordion__badge activity-log-sidebar-accordion__badge--${entry.actionType}`}>
                                                            {entry.action}
                                                        </span>
                                                    </div>
                                                    <XGSIcon
                                                        icon={isExpanded ? XGSIcons.faCaretUp : XGSIcons.faCaretDown}
                                                        className="activity-log-sidebar-accordion__chevron"
                                                        size="sm"
                                                    />
                                                </button>

                                                {isExpanded && (
                                                    <div className="activity-log-sidebar-accordion__body">
                                                        <div className="activity-log-sidebar-accordion__summary">{entry.summary}</div>
                                                        {entry.subtitle && (
                                                            <div className="activity-log-sidebar-accordion__subtitle">{entry.subtitle}</div>
                                                        )}
                                                        {entry.changeDetail && (
                                                            <div className="activity-log-sidebar-accordion__change">
                                                                {entry.changeDetail.label}:{' '}
                                                                <span className="activity-log-sidebar-accordion__change-old">
                                                                    {entry.changeDetail.oldValue}
                                                                </span>
                                                                {' → '}
                                                                <span className="activity-log-sidebar-accordion__change-new">
                                                                    {entry.changeDetail.newValue}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="activity-log-sidebar-accordion__meta">
                                                            <span className="activity-log-sidebar-accordion__time">{entry.time}</span>
                                                            {entry.weekLabel && (
                                                                <span className="activity-log-sidebar-accordion__week-badge">{entry.weekLabel}</span>
                                                            )}
                                                        </div>

                                                        <div className="activity-log-sidebar-accordion__details">
                                                            {entry.details.map((row) => (
                                                                <div key={row.label} className="activity-log-sidebar-accordion__detail-row">
                                                                    <span className="activity-log-sidebar-accordion__detail-label">{row.label}</span>
                                                                    <span className="activity-log-sidebar-accordion__detail-value">{row.value}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                        {isFetchingNextPage && <Loading isLoading={true} />}
                    </div>
                </Form>
            )}
        </Formik>
    </SlideOutSidebar >;
};

export default ActivityLogSidebar;
